import { useCallback, useRef, useState } from "react";
import { ApolloClient, gql, useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ContaminationDeclarationPrintFile } from "../graphql/queries/contaminationDeclarationPrintFile";
import { ContaminationDeclarationValidityFields } from "../graphql/fragments/contaminationDeclarationValidity";
import { openDownloadFallbackTab } from "../utils/downloadFile";
import { resolveErrorMessage } from "../utils/resolveErrorMessage";
import {
    PrintFileResult,
    PrintOutcome,
    runPrintDeclaration,
} from "../utils/contaminationDeclaration/printDeclaration";

// Everything the print query learned about printability — deliberately without
// `file`, whose presigned link must never enter the cache.
const ContaminationDeclarationPrintState = gql`
    fragment ContaminationDeclarationPrintState on ContaminationDeclaration {
        id
        declarationAccepted
        validity {
            ...ContaminationDeclarationValidityFields
        }
    }
    ${ContaminationDeclarationValidityFields}
`;

/**
 * Writes the authoritative printability back onto the cached declaration, so
 * the status chips, the editor footer and the other entry points correct
 * themselves the moment a print attempt discovers the declaration is no longer
 * printable. They all read the same normalized entity, so this needs neither a
 * refetch nor a callback threaded through the call sites.
 */
const writeBackPrintState = (client: ApolloClient<object>, id: string, declaration: PrintFileResult) => {
    try {
        client.cache.writeFragment({
            id: client.cache.identify({ __typename: 'ContaminationDeclaration', id }),
            fragment: ContaminationDeclarationPrintState,
            fragmentName: 'ContaminationDeclarationPrintState',
            data: {
                __typename: 'ContaminationDeclaration',
                id,
                declarationAccepted: declaration.declarationAccepted ?? null,
                validity: declaration.validity,
            },
        });
    } catch {
        // A cache write must never cost the user the print they asked for.
    }
};

/**
 * Opens the rendered declaration PDF in a new tab, where the user prints it
 * (the document is cross-origin, so `tab.print()` is not ours to call).
 *
 * The link is presigned, user-bound and short-lived, so it is fetched fresh on
 * every click and never cached. The tab is opened *before* that round trip —
 * see `runPrintDeclaration`, which owns the order and is tested on its own.
 */
export const useContaminationDeclarationPrint = () => {
    const { t } = useTranslation();
    const client = useApolloClient();
    const busyRef = useRef(false);
    const [busy, setBusy] = useState(false);

    const print = useCallback(async (declarationId: string): Promise<PrintOutcome | undefined> => {
        // A ref, not the state: two clicks in quick succession would both pass
        // a `busy` that has not re-rendered yet, and open two tabs.
        if (busyRef.current) return undefined;
        busyRef.current = true;
        setBusy(true);

        try {
            return await runPrintDeclaration({
                openTab: () => {
                    const tab = openDownloadFallbackTab(t('Printing…'));
                    if (!tab) return null;
                    return {
                        get closed() { return tab.closed; },
                        close: () => tab.close(),
                        // `replace`, not `href`: no history entry, so the user
                        // cannot navigate back to a blank tab. Cross-origin
                        // callable, unlike `assign`.
                        navigate: (url: string) => tab.location.replace(url),
                    };
                },
                fetchPrintFile: async () => {
                    const res = await client.query({
                        query: ContaminationDeclarationPrintFile,
                        variables: { id: declarationId },
                        // no-cache, not network-only: `File` is a normalized entity
                        // shared across queries, so caching would hand this
                        // expiring, user-bound link to every screen reading it.
                        fetchPolicy: "no-cache",
                    });
                    return res.data?.contaminationDeclaration;
                },
                onState: (result) => writeBackPrintState(client, declarationId, result),
                onError: (outcome, err) => {
                    if (outcome === 'popupBlocked') {
                        toast.error(t('Please allow pop-ups for this site to print the declaration.'));
                    } else if (outcome === 'notPrintable') {
                        toast.error(t('This declaration is no longer ready to print.'));
                    } else {
                        toast.error(resolveErrorMessage(err, t));
                    }
                },
            });
        } finally {
            busyRef.current = false;
            setBusy(false);
        }
    }, [client, t]);

    return { print, busy };
};
